import React, { useEffect, useState } from "react";
import { ISalary, SalaryType } from "../../types/salary.type";
import { api } from "../../api/api";
import {
  Button,
  Checkbox,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Menu,
  Modal,
  Select,
  Space,
  Table,
  Typography,
  message,
} from "antd";
import { DownOutlined, HistoryOutlined } from "@ant-design/icons";
import { Cashbox } from "../../types/cashbox.type";
import useGetRequest from "../../hooks/useGetRequest";
import { useNavigate } from "react-router-dom";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import { useDownloadFile } from "../../hooks/useDownloadFile";
import { useAuth } from "../../contexts/auth.context";
import { UserRole } from "../../types/user.type";
import { Currency } from "../../types/currency.type";
import { Machine } from "../../types/machine.type";
import { useExchangeRate } from "../../contexts/exchangeRate.context";
import WorkerSalaryHistory from "../../components/WorkerSalaryHistory/workerSalaryHistory.component";

const { Title } = Typography;
const { Search } = Input;

export default function Salary() {
  const { authState } = useAuth();
  const { exchangeRate } = useExchangeRate();
  const navigate = useNavigate();
  const [salaries, setSalaries] = useState<ISalary[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [hasDebt, setHasDebt] = useState<boolean | undefined>(undefined);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPerDayOpen, setIsPerDayOpen] = useState(false);
  const [isTransactionOpen, setIsTransactionOpen] = useState(false);
  const [isEditDebtPaidOpen, setIsEditDebtPaidOpen] = useState(false);
  const [isSalaryHistoryOpen, setIsSalaryHistoryOpen] = useState(false);
  const [selectedSalary, setSelectedSalary] = useState<ISalary | null>(null);
  const [currency, setCurrency] = useState<string | undefined>(undefined);
  const [salaryType, setSalaryType] = useState<SalaryType | undefined>();
  const [form] = Form.useForm();
  const [perDayForm] = Form.useForm();
  const [transactionForm] = Form.useForm();
  const [editDebtPaidForm] = Form.useForm();
  const { data: cashboxesList, loading: cashboxesListLoading } =
    useGetRequest(`/cashbox/list`);
  const { data: machineList, loading: machineListLoading } =
    useGetRequest("/machine/list");
  const { loading: downloadLoading, downloadFile } = useDownloadFile();

  const handleExport = () => {
    downloadFile("/export/salaries", "salaries_data.xlsx");
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleEditPerDay = async (values: { perDay: number }) => {
    setConfirmLoading(true);

    try {
      await api.put(`/salary/per-day/${selectedSalary?.workerId}`, values);
      message.success("Цены успешно обновлены.");
      fetchSalaries();
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error creating report", error);
      message.error(error.response.data.error);
    } finally {
      setConfirmLoading(false);
      setIsPerDayOpen(false);
    }
  };

  const handleSubmitForm = (values: any) => {
    setConfirmLoading(true);

    const updatedPricings = selectedSalary?.pricings.map((pricing) => {
      const formData = values[pricing._id];

      const updatedPricing = {
        forName: formData.forName || pricing.forName,
        ...(selectedSalary?.salaryType === SalaryType.PERMONTH
          ? { perMonth: formData.perMonth }
          : { perUnit: formData.perUnit }),
      };

      return updatedPricing;
    });

    api
      .put(`/salary/${selectedSalary?.workerId}`, { pricings: updatedPricings })
      .then(() => {
        message.success("Цены успешно обновлены.");
        setIsModalOpen(false);
        fetchSalaries();
        setConfirmLoading(false);
      })
      .catch((error) => {
        console.error("Error updating prices:", error);
        message.error(error.response.data.error);
        setConfirmLoading(false);
      });
  };

  const handleTransactionCreate = async (values: any) => {
    setConfirmLoading(true);
    try {
      const data = {
        ...values,
        exchangeRate: exchangeRate || values.exchangeRate,
      };

      await api.post(`/salary/${selectedSalary?._id}/transaction`, data);

      message.success("Оплата успешно создана.");

      setIsTransactionOpen(false);
      setConfirmLoading(false);
      fetchSalaries();
      transactionForm.resetFields();
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error saving cashbox:", error);
      message.error(error.response.data.error);
    }
  };

  const handleAddPricing = () => {
    const newPricing: ISalary["pricings"][0] = {
      _id: Date.now().toString(),
      forName: "",
      perUnit:
        selectedSalary?.salaryType === SalaryType.PERUNIT ? 0 : undefined,
      perMonth:
        selectedSalary?.salaryType === SalaryType.PERMONTH ? 0 : undefined,
    };
    const updatedPricings = selectedSalary?.pricings.concat(newPricing);

    setSelectedSalary({
      ...selectedSalary!,
      pricings: updatedPricings!,
    });

    form.setFieldsValue({
      [newPricing._id]: {
        forName: newPricing.forName,
        perUnit: newPricing.perUnit,
        perMonth: newPricing.perMonth,
      },
    });
  };

  const handleModalOpen = (salary: ISalary) => {
    if (salary.salaryType === SalaryType.PERDAY) {
      setIsPerDayOpen(true);
      setSelectedSalary(salary);
      perDayForm.setFieldsValue({ perDay: salary.perDay });
    } else {
      setIsModalOpen(true);
      setSelectedSalary(salary);

      const initialValues = salary.pricings.reduce(
        (acc: { [key: string]: any }, pricing) => {
          acc[pricing._id] = {
            forName: pricing.forName,
            perMonth: pricing.perMonth,
            perUnit: pricing.perUnit,
          };
          return acc;
        },
        {}
      );

      form.setFieldsValue(initialValues);
    }
  };

  const handleTransactionClick = (salary: ISalary) => {
    setIsTransactionOpen(true);
    setSelectedSalary(salary);
  };

  const handleEditDebtPaidClick = (salary: ISalary) => {
    setIsEditDebtPaidOpen(true);
    setSelectedSalary(salary);
    editDebtPaidForm.setFieldsValue({
      debt: salary.debt,
      paid: salary.paid,
    });
  };

  const handleSalaryHistoryClick = (salary: ISalary) => {
    setIsSalaryHistoryOpen(true);
    setSelectedSalary(salary);
  };

  const handleEditDebtPaid = async (values: { debt: number; paid: number }) => {
    setConfirmLoading(true);
    try {
      await api.put(`/salary/${selectedSalary?.workerId}/debt-paid`, values);
      message.success("Долг и оплата успешно обновлены.");
      fetchSalaries();
      setIsEditDebtPaidOpen(false);
    } catch (error: any) {
      console.error("Error updating debt and paid:", error);
      message.error(error.response?.data?.error || "Ошибка при обновлении");
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleDeletePricing = (pricingId: string) => {
    const updatedPricings = selectedSalary?.pricings.filter(
      (pricing) => pricing._id !== pricingId
    );

    setSelectedSalary({
      ...selectedSalary!,
      pricings: updatedPricings!,
    });
  };

  const fetchSalaries = async () => {
    setLoading(true);
    try {
      const response = await api.get("/salary", {
        params: {
          page,
          limit: pageSize,
          search: searchTerm,
          hasDebt: hasDebt ? true : undefined,
          currency,
          salaryType,
        },
      });

      setSalaries(response.data.salaries);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching salaries", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalaries();
  }, [page, searchTerm, pageSize, hasDebt, currency, salaryType]);

  useEffect(() => {
    if (exchangeRate) {
      form.setFieldsValue({ exchangeRate });
    }
  }, [exchangeRate, form]);

  const columns = [
    {
      title: "Имя",
      dataIndex: "workerName",
      key: "workerName",
      fixed: "left" as "left",
    },
    {
      title: "Фамилия",
      dataIndex: "workerSurname",
      key: "workerSurname",
    },
    {
      title: "Тип зарплаты",
      dataIndex: "salaryType",
      key: "salaryType",
    },
    {
      title: "Валюта зарплаты",
      dataIndex: "salaryCurrency",
      key: "salaryCurrency",
    },
    {
      title: "Заплатили",
      dataIndex: "paid",
      key: "paid",
    },
    {
      title: "Надо заплатить",
      dataIndex: "debt",
      key: "debt",
    },
    {
      title: "Цены",
      key: "pricings",
      render: (_: any, record: ISalary) => {
        return (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              maxWidth: "1400px",
            }}
          >
            {record.pricings.map((pricing, index) => (
              <p key={index}>
                {index + 1}. {pricing.forName}, Ставка:{pricing.perUnit}
                {pricing.perMonth}
                {pricing.quantity
                  ? `, Кол-во выполнено: ${pricing.quantity}`
                  : null}
              </p>
            ))}
          </div>
        );
      },
    },
    {
      title: "За день",
      dataIndex: "perDay",
      key: "perDay",
    },
    {
      title: "Начало",
      dataIndex: "periodStart",
      key: "periodStart",
    },
    {
      title: "Обновлено",
      dataIndex: "lastUpdate",
      key: "lastUpdate",
    },
    {
      title: "",
      key: "actions",
      render: (_: any, record: ISalary) => {
        const menu = (
          <Menu>
            {(authState.user?.role === UserRole.Admin ||
              authState.user?.role === UserRole.Accountant) && (
              <Menu.Item onClick={() => handleModalOpen(record)}>
                Изменить цену
              </Menu.Item>
            )}

            {authState.user?.role === UserRole.Admin && (
              <Menu.Item onClick={() => handleEditDebtPaidClick(record)}>
                Изменить долг и оплату
              </Menu.Item>
            )}

            {(authState.user?.role === UserRole.Admin ||
              authState.user?.role === UserRole.Cashier ||
              authState.user?.role === UserRole.Accountant) && (
              <>
                <Menu.Item onClick={() => handleTransactionClick(record)}>
                  Добавить оплату
                </Menu.Item>
                <Menu.Item
                  onClick={() =>
                    navigate(
                      `/${record.workerId}/salary/${record.workerName}/USD&UZS/transactions`
                    )
                  }
                >
                  Смотреть транзакции
                </Menu.Item>
              </>
            )}

            <Menu.Item onClick={() => handleSalaryHistoryClick(record)}>
              <Space>
                <HistoryOutlined />
                История зарплат
              </Space>
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Зарплата</Title>
      <Space style={{ marginBottom: 16 }}>
        <Search
          placeholder="Поиск по названию"
          onSearch={handleSearch}
          allowClear
        />
        <Select
          allowClear
          placeholder="Фильтр по валюте"
          onChange={(value) => setCurrency(value)}
          style={{ width: 150 }}
        >
          {Object.values(Currency).map((currency) => (
            <Select.Option key={currency} value={currency}>
              {currency}
            </Select.Option>
          ))}
        </Select>

        <Select
          allowClear
          placeholder="Фильтр по типу"
          onChange={(value) => setSalaryType(value)}
          style={{ width: 150 }}
        >
          {Object.values(SalaryType).map((type) => (
            <Select.Option key={type} value={type}>
              {type}
            </Select.Option>
          ))}
        </Select>
        <Checkbox
          checked={hasDebt}
          onChange={(e) => setHasDebt(e.target.checked)}
        >
          Есть долг
        </Checkbox>
        <Button
          type="default"
          loading={downloadLoading}
          icon={<PiMicrosoftExcelLogoFill />}
          onClick={handleExport}
        >
          Экспорт в Excel
        </Button>
      </Space>
      <Table
        loading={loading}
        columns={columns}
        dataSource={salaries}
        rowKey={(record) => record._id}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
        }}
        scroll={{ x: "max-content" }}
      />

      <Modal
        title="Изменить цены"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={form} onFinish={handleSubmitForm} layout="vertical">
          {selectedSalary?.pricings.map((pricing) => (
            <div key={pricing._id}>
              <Form.Item name={[pricing._id, "forName"]} label="Название">
                <Input />
              </Form.Item>

              {selectedSalary?.salaryType === SalaryType.PERMONTH ? (
                <Form.Item
                  name={[pricing._id, "perMonth"]}
                  label="Ставка за месяц"
                >
                  <InputNumber min={0} />
                </Form.Item>
              ) : (
                <Form.Item
                  name={[pricing._id, "perUnit"]}
                  label="Ставка за единицу"
                >
                  <InputNumber min={0} />
                </Form.Item>
              )}

              {pricing.for === undefined && (
                <Button onClick={() => handleDeletePricing(pricing._id)}>
                  Удалить
                </Button>
              )}
            </div>
          ))}

          <Button type="primary" onClick={handleAddPricing}>
            Добавить новый прайсинг
          </Button>
        </Form>
      </Modal>

      <Modal
        title="Добавить оплату"
        open={isTransactionOpen}
        onCancel={() => setIsTransactionOpen(false)}
        onOk={() => transactionForm.submit()}
        confirmLoading={confirmLoading}
      >
        <Form
          form={transactionForm}
          onFinish={handleTransactionCreate}
          layout="vertical"
        >
          <Form.Item
            name="cashboxId"
            label="Касса"
            rules={[{ required: true, message: "Выберите кассу" }]}
          >
            <Select placeholder="Выберите кассу" loading={loading}>
              {cashboxesList?.map((cashbox: Cashbox) => (
                <Select.Option key={cashbox._id} value={cashbox._id}>
                  Имя: {cashbox.name} | Валюта: {cashbox.currency}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="machineId"
            label="Станок"
            rules={[{ required: true, message: "Выберите станок" }]}
          >
            <Select
              placeholder="Выберите станок"
              allowClear
              loading={machineListLoading}
            >
              {machineList?.map((machine: Partial<Machine>) => (
                <Select.Option key={machine._id} value={machine._id}>
                  {machine.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="amount"
            label="Сумма"
            rules={[{ required: true, message: "Введите сумму" }]}
          >
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item
            name="exchangeRate"
            label="Курс"
            hidden={true}
          >
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item
            name="description"
            label="Описание"
            rules={[{ required: true, message: "Введите описание." }]}
          >
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Цена в день"
        open={isPerDayOpen}
        onCancel={() => setIsPerDayOpen(false)}
        onOk={() => perDayForm.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={perDayForm} layout="vertical" onFinish={handleEditPerDay}>
          <Form.Item
            name="perDay"
            label="Цена в день"
            rules={[
              {
                required: true,
                message: "Пожалуйста, введите цену",
              },
            ]}
          >
            <InputNumber min={0} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Изменить долг и оплату"
        open={isEditDebtPaidOpen}
        onCancel={() => setIsEditDebtPaidOpen(false)}
        onOk={() => editDebtPaidForm.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={editDebtPaidForm} layout="vertical" onFinish={handleEditDebtPaid}>
          <Form.Item
            name="debt"
            label="Долг"
            rules={[
              {
                required: true,
                message: "Пожалуйста, введите долг",
              },
            ]}
          >
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="paid"
            label="Оплачено"
            rules={[
              {
                required: true,
                message: "Пожалуйста, введите сумму оплаты",
              },
            ]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Модальное окно истории зарплат работника */}
      <WorkerSalaryHistory
        workerId={selectedSalary?.workerId || ""}
        workerName={selectedSalary?.workerName || ""}
        workerSurname={selectedSalary?.workerSurname}
        visible={isSalaryHistoryOpen}
        onClose={() => setIsSalaryHistoryOpen(false)}
      />
    </div>
  );
}
